// The frame every widget sits in: a quiet header (status dot, title, where the
// data came from, how fresh it is) and a body. Data widgets go through a gate
// that shows the real reason when there is nothing to show.

import { hasStopped } from "../../shared/agentStatus";
import { Component, createContext, memo, useContext, useMemo, type ReactNode } from "react";
import { MoreHorizontal, RefreshCw, TriangleAlert } from "lucide-react";
import { RETIRED_TYPES, SPEC, readTasks, untitled } from "../../shared/catalog";
import { timeAgo } from "../../shared/format";
import type { Activity, AgentState, ComputerConnections, FileCopy, Pull, RunnerSession, RunnerState, Stream, WidgetItem } from "../../shared/types";
import { KEY_SERVICE } from "../../shared/keyServices";
import { PRESETS } from "../../shared/presets";
import { cx } from "../lib/util";
import { readableOn } from "../themes/themes";
import { htmlHash, problems } from "../../shared/widgetKit";
import { get, set, useStore } from "../state/store";
import { refreshNow, useRefreshing } from "../state/sources";
import { openContextMenu } from "../chrome/ContextMenu";
import { RENDER, LOCAL_TYPES } from "./registry";
import { useTick } from "./tick";
import { EmptyData, Misfit, misfit } from "./empty";
import { MapSetup } from "./setup";
import { useJobRunning } from "./job";
import { DataSwitch } from "./DataControl";
import { SignInAgain, useSignedOut } from "./SignInAgain";
import { ownsTouch } from "../phone/gestures";

export { useTick } from "./tick";

// a button's title is its words, on the button itself
const NO_HEADER = new Set(["heading", "sticky", "image", "action"]);

export function tileKindFor(item: WidgetItem): string {
  // a Button is just the button, whatever tile was picked for it (Round 7)
  if (item.type === "action") return "bare";
  const t = item.style?.tile;
  if (t) return t;
  if (item.type === "heading") return "bare";
  if (item.type === "sticky") return "paper";
  return "theme";
}

export function headerFor(item: WidgetItem): boolean {
  if (item.type === "action") return false;
  if (item.style?.header !== undefined && item.style?.header !== null) return item.style.header;
  if (NO_HEADER.has(item.type)) return false;
  if (item.type === "clock" && item.props.face === "analog") return false;
  return true;
}

export function sourceLabel(stream: Stream | null, pullName?: string | null): string | null {
  if (!stream) return null;
  if (pullName) return pullName;
  if (stream.updatedBy) return stream.updatedBy;
  return null;
}

/**
 * What a widget fed by a read is called when nobody named it (its title empty, or its type's own name): the read's
 * name, as Connect gave it ("Sessions", "Orders"), or the name its stream was sent with. Null when it has neither.
 */
export function readName(item: WidgetItem, stream: Stream | null, pull: Pull | null | undefined): string | null {
  if (!untitled(item)) return null;
  return pull?.name?.trim() || stream?.label?.trim() || null;
}

/** Where a read comes from, in a word: the service its key or sign-in is for, the free source, or the address's host. */
export function readFrom(pull: Pull, connection: string | null): string | null {
  if (pull.config.service && Object.hasOwn(KEY_SERVICE, pull.config.service)) return KEY_SERVICE[pull.config.service].name;
  if (pull.config.connection) return connection;
  if (pull.kind === "preset") return PRESETS.find((p) => p.id === pull.config.preset)?.label ?? null;
  try {
    return pull.config.url ? new URL(pull.config.url).hostname.replace(/^www\./, "") : null;
  } catch {
    return null;
  }
}

/** The light on an Agent widget's head: amber while a session or an agent waits on the user, live while one works. */
function agentLight(agents: AgentState[], sessions: RunnerSession[]): "live" | "warn" | "idle" {
  const day = Date.now() - 24 * 3_600_000;
  if (sessions.some((x) => x.state === "needs_you") || agents.some((a) => (a.status === "needs_you" || a.status === "failed") && a.updatedAt > day)) return "warn";
  if (sessions.some((x) => x.state === "working") || agents.some((a) => a.status === "working" && !hasStopped(a))) return "live";
  return "idle";
}

/** Widgets that keep their own reading in a stream, though the board counts them as the user's own. */
const OWN_FEED = new Set(["calendar", "weather"]);
/** Widgets the user's computers keep current while one is on, with no time of their own. */
const FROM_COMPUTER = new Set(["agent", "needsyou", "gitlog"]);

/**
 * How fresh a widget's live data is, for its head (or its corner, with no head): when its read last came in, when
 * Claude Code or Codex last checked its plan, or whether the computer that keeps it current is on. Null for a widget
 * with nothing live, and for the owner's own widgets on a shared view, which show nothing of theirs.
 */
function useFresh(item: WidgetItem, stream: Stream | null, readOnly: boolean): { at: number; tip: string } | { word: string; tip: string } | null {
  const sample = useSample();
  const store = useStore((s) => s.runner);
  const runner = sample?.runner ?? store;
  if ((!LOCAL_TYPES.has(item.type) || OWN_FEED.has(item.type)) && stream?.updatedAt) return { at: stream.updatedAt, tip: `Updated ${timeAgo(stream.updatedAt)}${stream.updatedBy ? ` by ${stream.updatedBy}` : ""}` };
  if (readOnly && !sample) return null;
  if (item.type === "usage") {
    const at = Math.max(runner?.limits?.claude?.at ?? 0, runner?.limits?.codex?.at ?? 0);
    return at ? { at, tip: `When Claude Code or Codex last checked its plan, ${timeAgo(at)}` } : null;
  }
  // git says nothing until it has repos to show
  if (FROM_COMPUTER.has(item.type) && runner && (item.type !== "gitlog" || runner.git?.length)) {
    const on = (runner.computers ?? []).filter((c) => c.online);
    if (runner.online || on.length) return { word: "Live", tip: `Kept up to date while ${on.map((c) => c.host).join(" and ") || runner.host || "your computer"} ${on.length > 1 ? "are" : "is"} on` };
    if (runner.computers?.length) return { word: "Computer off", tip: "What your computer last sent. It updates again once it is on." };
  }
  return null;
}

/** "Updated 2m ago", or "2m ago" in a narrow tile; "Live" for what a computer keeps current. */
function freshText(fresh: { at: number } | { word: string }, narrow: boolean): string {
  if ("word" in fresh) return fresh.word;
  const ago = timeAgo(fresh.at);
  return narrow ? ago : `Updated ${ago}`;
}

/**
 * A headerless widget's freshness, in its top corner beside the menu, on hover: a custom widget draws everything else
 * itself, so this is where its refresh is, with no change to its code (Round 7).
 */
function CornerFresh({ item, stream, readOnly }: { item: WidgetItem; stream: Stream | null; readOnly: boolean }) {
  useTick();
  const sample = useSample();
  const fresh = useFresh(item, stream, readOnly);
  const pull = useStore((s) => (stream?.pullId ? s.pulls[stream.pullId] : null));
  if (!fresh) return null;
  if (pull && "at" in fresh && !readOnly && !sample) return <RefreshButton pullId={pull.id} text={freshText(fresh, true)} tip={stream?.error ? `Could not refresh ${timeAgo(stream.errorAt)}: ${stream.error}` : fresh.tip} corner />;
  return (
    <span className={cx("tile-fresh tile-fresh-float", readOnly && "is-alone")} title={fresh.tip}>
      {freshText(fresh, true)}
    </span>
  );
}

function TileHeader({ item, stream, readOnly }: { item: WidgetItem; stream: Stream | null; readOnly: boolean }) {
  useTick();
  const sample = useSample();
  const pull = useStore((s) => (stream?.pullId ? s.pulls[stream.pullId] : null));
  // the server a read signs in to, by name, for the header of a widget nobody named
  const connection = useStore((s) => (pull?.config.connection ? (s.connections[pull.config.connection]?.name ?? null) : null));
  // an Agent widget's light: amber while anything waits on the user, live while anything works
  const storeAgentState = useStore((s) => (item.type === "agent" && !readOnly ? agentLight(Object.values(s.agents), s.runner?.sessionsOn ? (s.runner.sessions ?? []) : []) : null));
  const agentState = sample ? "live" : storeAgentState;
  const local = LOCAL_TYPES.has(item.type);
  const html = item.type === "custom" ? String(item.props.html ?? "") : "";
  const hash = useMemo(() => (html ? htmlHash(html) : ""), [html]);
  const custom = useStore((s) => {
    const r = hash ? s.renders[item.id] : undefined;
    return r && r.hash === hash ? r.report : null;
  });
  // its job running now on the user's computer: the tile says so until it is done (a brief and an Agent job say it in their own first line)
  const running = useJobRunning(item.id) > 0 && !readOnly && !sample && item.type !== "brief" && item.type !== "job";
  // its connection signed out: Sign in, beside the last good value (a tile with nothing yet says it in its body)
  const out = useSignedOut(stream);
  const signedOut = readOnly || sample ? null : out;
  let state: "live" | "idle" | "warn" | "bad" | "local" = local ? "local" : "idle";
  let sub: string | null = null;
  const own = useFresh(item, stream, readOnly);
  // "Updated" goes when the tile is too narrow for it beside the title
  let fresh: string | null = own ? freshText(own, item.w < 360) : null;
  let tip: string | undefined = own?.tip;
  if (!local && stream) {
    // a Google read says whose it is: its own name is the widget's title, and its period is on the switch
    sub = pull?.kind === "google" ? "Google Analytics" : pull?.kind === "search-console" ? "Search Console" : pull?.config.ours ? (pull.config.ours.read.startsWith("seo-") ? "ContextBolt SEO" : "ContextBolt Bookmarks") : sourceLabel(stream, pull?.name);
    if (stream.updatedAt) {
      const stale = pull ? Date.now() - stream.updatedAt > Math.max(pull.every * 3000, 15 * 60_000) : false;
      state = stream.error ? "warn" : stale ? "warn" : "live";
      if (stale && !stream.error) tip += ". Older than expected.";
    }
    if (stream.error) {
      state = stream.updatedAt ? "warn" : "bad";
      tip = `Could not refresh ${timeAgo(stream.errorAt)}: ${stream.error}`;
    }
  }
  if (item.type === "agent" && agentState) state = agentState;
  if (item.type === "feed" && item.props.source !== "stream") {
    sub = "Board";
    state = "live";
  }
  // a custom widget's own health outranks how fresh its data is
  if (custom) {
    const issues = problems(custom);
    if (custom.errors.length) {
      state = "bad";
      tip = `It hit an error when it last drew: ${custom.errors[0].message}`;
      fresh = null;
    } else if (issues.length) {
      state = "warn";
      tip = issues[0];
    }
  }
  // a task list says how far along it is, as on the landing page: 2 of 5
  if (item.type === "tasks") {
    const all = readTasks(item.props.tasks);
    if (all.length) sub = `${all.filter((t) => t.done).length} of ${all.length}`;
  }
  if (item.by) sub = sub ?? `by ${item.by}`;
  if (running) state = "live";
  // a widget nobody named says what it shows (Sessions), and where that comes from beside it (Lemon Squeezy)
  const named = local ? null : readName(item, stream, pull);
  const title = named ?? (item.title || SPEC[item.type].label);
  // a title that is the read's name already has it said: beside it goes where it comes from, not the name again
  if (pull && sub === pull.name && sub.trim().toLowerCase() === title.trim().toLowerCase()) sub = readFrom(pull, connection);
  // "Claude · Claude" says nothing twice
  if (sub && sub.trim().toLowerCase() === title.trim().toLowerCase()) sub = null;
  return (
    <div className="tile-head" data-drag-handle>
      <span className={cx("dot", `dot-${state}`)} />
      <span className="tile-title">{title}</span>
      {sub && <span className="tile-sub">{sub}</span>}
      <span className="tile-head-end">
        {!sample && <DataSwitch item={item} stream={stream} readOnly={readOnly} />}
        {(stream?.error || (custom && state !== "live" && state !== "local" && state !== "idle")) && <TriangleAlert size={12} className="warn-ico" aria-label={tip} />}
        {signedOut && stream?.updatedAt ? (
          <SignInAgain conn={signedOut} small />
        ) : running ? (
          <span className="tile-running" title="Running on your computer now">
            <span className="spinner" /> Running
          </span>
        ) : fresh && own && "at" in own && pull && !readOnly && !sample ? (
          <RefreshButton pullId={pull.id} text={fresh} tip={tip} />
        ) : (
          fresh && (
            <span className="tile-fresh" title={tip}>
              {fresh}
            </span>
          )
        )}
        {!readOnly && <TileMenuButton id={item.id} />}
      </span>
    </div>
  );
}

/**
 * A widget that could not draw says so in its tile, and draws again as soon as
 * what it draws from changes: an agent's fix, a tidier copy from the board, or
 * new data, with no reload. what: the widget and its stream.
 */
class Boundary extends Component<{ children: ReactNode; label: string; what: readonly unknown[] }, { error: string | null; what: readonly unknown[] }> {
  state = { error: null as string | null, what: this.props.what };
  static getDerivedStateFromError(e: unknown) {
    return { error: e instanceof Error ? e.message : "it broke" };
  }
  static getDerivedStateFromProps(p: { what: readonly unknown[] }, s: { what: readonly unknown[] }) {
    const changed = p.what.length !== s.what.length || p.what.some((x, i) => x !== s.what[i]);
    return changed ? { error: null, what: p.what } : null;
  }
  render() {
    if (this.state.error)
      return (
        <div className="widget-error">
          <b>This widget could not draw.</b>
          <span>{this.state.error}</span>
        </div>
      );
    return this.props.children;
  }
}

// Previews in the gallery hand widgets example data through this, so the real
// widgets draw them without anything touching the board's own state.
export const SampleCtx = createContext<{ agents?: Record<string, AgentState>; activity?: Activity[]; runner?: RunnerState; files?: Record<string, FileCopy>; connections?: ComputerConnections[] } | null>(null);
export const useSample = () => useContext(SampleCtx);

/** The widget's menu, opened from its button: the same one a right-click opens. */
/**
 * A widget's "Updated 12m ago", pressed, reads its source again now (David, 21 Oct): sources read on their own only
 * for the board in view, every half hour by default, so a number wanted this minute is one press away. Its arrow
 * always shows, so it reads as a button and not a time (Round 7); it spins while reading.
 */
function RefreshButton({ pullId, text, tip, corner }: { pullId: string; text: string; tip?: string; corner?: boolean }) {
  const busy = useRefreshing((s) => !!s[pullId]);
  return (
    <button
      type="button"
      className={cx("tile-fresh tile-refresh", corner && "tile-fresh-float is-button", busy && "is-busy")}
      title={tip ? `${tip}. Press to refresh now` : "Refresh now"}
      aria-label={`${text}. Refresh now`}
      aria-busy={busy}
      disabled={busy}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        void refreshNow(pullId);
      }}
    >
      <RefreshCw size={11} className="tile-refresh-ico" aria-hidden />
      {busy ? "Refreshing" : text}
    </button>
  );
}

function TileMenuButton({ id, float }: { id: string; float?: boolean }) {
  return (
    <button
      className={cx("tile-menu", float && "tile-menu-float")}
      aria-label="Widget menu"
      data-tip={float ? "Widget menu" : undefined}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        if (!get().selection.includes(id)) set({ selection: [id] });
        openContextMenu({ x: r.right - 4, y: r.bottom + 6, kind: "item", ids: [id], at: null, align: "right" });
      }}
    >
      <MoreHorizontal size={15} />
    </button>
  );
}

/**
 * A right-click on a widget opens its menu wherever it sits: beside a calendar, pipeline or sheet, or in a doc's
 * page (David, 16 Oct). On a canvas the canvas opens it, with where on the board it was asked.
 */
function tileContextMenu(id: string, readOnly: boolean) {
  return (e: React.MouseEvent) => {
    if (readOnly || (e.currentTarget as HTMLElement).closest(".viewport")) return;
    // a phone's long press in words being written selects them, as anywhere on a phone
    if (get().mobile && ownsTouch(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    if (!get().selection.includes(id)) set({ selection: [id] });
    openContextMenu({ x: e.clientX, y: e.clientY, kind: "item", ids: [id], at: null });
  };
}

export const WidgetTile = memo(function WidgetTile({ item, readOnly, stream }: { item: WidgetItem; readOnly: boolean; stream?: Stream | null }) {
  // a widget this page cannot draw (from a newer board, or a type that has gone) is a quiet card, never a broken board
  if (!Object.hasOwn(SPEC, item.type) || !Object.hasOwn(RENDER, item.type))
    return (
      <div className="tile tile-theme no-header" onContextMenu={tileContextMenu(item.id, readOnly)}>
        {!readOnly && <TileMenuButton id={item.id} float />}
        <div className="tile-body">
          <div className="empty">
            <span className="empty-title">{item.title || "A widget"}</span>
            <span className="empty-sub">This page cannot draw this kind of widget. Reload to get the newest board.</span>
          </div>
        </div>
      </div>
    );
  return <KnownTile item={item} readOnly={readOnly} stream={stream} />;
});

function KnownTile({ item, readOnly, stream: override }: { item: WidgetItem; readOnly: boolean; stream?: Stream | null }) {
  const fromStore = useStore((s) => (item.stream ? (s.streams[item.stream] ?? null) : null));
  const stream = override !== undefined ? override : fromStore;
  const glowAt = useStore((s) => s.glow[item.id] ?? 0);
  const kind = tileKindFor(item);
  const header = headerFor(item);
  const Render = RENDER[item.type];
  const spec = SPEC[item.type];
  // a custom widget with a stream waits for it like any other; one without works things out itself
  // a retired widget says so itself rather than asking to be connected
  const needsData = item.type === "custom" ? !!item.stream && !!item.props.html : spec.reads !== null && !LOCAL_TYPES.has(item.type) && !RETIRED_TYPES.has(item.type) && !(item.type === "feed" && item.props.source !== "stream");
  const hasData = !!stream && stream.updatedAt > 0;
  const preview = useStore((s) => (s.tintPreview?.ids.includes(item.id) ? s.tintPreview.color : null));
  const tint = preview ?? item.style?.accent ?? null;
  const style = tint
    ? ({ "--accent": tint, "--chart-1": tint, "--accent-soft": `color-mix(in srgb, ${tint} 16%, transparent)`, "--accent-line": `color-mix(in srgb, ${tint} 50%, transparent)`, "--accent-fg": readableOn(tint) } as React.CSSProperties)
    : undefined;
  const fresh = glowAt && Date.now() - glowAt < 4000;
  // a Button keeps its settings behind its own gear, and has no age to show
  const button = item.type === "action";
  return (
    <div className={cx("tile", `tile-${kind}`, !header && "no-header", `size-${item.style?.size ?? "m"}`, tint && "is-tinted", button && "is-button")} style={style} onContextMenu={tileContextMenu(item.id, readOnly)}>
      {fresh ? <span className="tile-glow" key={glowAt} /> : null}
      {header && <TileHeader item={item} stream={stream} readOnly={readOnly} />}
      {/* a widget with no header (a timer's face, a sticky) keeps its menu in a corner, shown on hover */}
      {!header && !readOnly && !button && <TileMenuButton id={item.id} float />}
      {!header && !button && <CornerFresh item={item} stream={stream} readOnly={readOnly} />}
      <div className="tile-body">
        <Boundary label={item.type} what={[item, stream]}>
          {needsData && !hasData ? item.type === "map" && !item.stream && !readOnly ? <MapSetup item={item} /> : <EmptyData item={item} stream={stream} readOnly={readOnly} /> : needsData && stream && misfit(item, stream) ? <Misfit item={item} stream={stream} readOnly={readOnly} /> : <Render item={item} stream={stream} readOnly={readOnly} />}
        </Boundary>
      </div>
    </div>
  );
}
